import { Box, Tooltip, Typography } from "@mui/material";
import { HorizontalBox } from "src/components/common/box/HorizontalBox.tsx";
import i18n from "i18next";

export type SalesLeadFollowUpStatus = "overdue" | "no_next_step" | "on_track";

export interface SalesLeadFollowUpStatusIndicatorProps {
	status: string | null | undefined;
	showLabel?: boolean;
}

/**
 * Shows the follow-up status that `sales_lead_view` computes for open leads as a traffic light.
 */
export function SalesLeadFollowUpStatusIndicator({ status, showLabel = false }: SalesLeadFollowUpStatusIndicatorProps) {
	if (!isSalesLeadFollowUpStatus(status)) return null;
	return (
		<Tooltip title={getSalesLeadFollowUpStatusDescription(status)}>
			<HorizontalBox sx={{ gap: 1, alignItems: "center" }}>
				<Box
					sx={{
						width: 12,
						height: 12,
						flexShrink: 0,
						borderRadius: "50%",
						backgroundColor: followUpStatusColors[status],
					}}
				/>
				{showLabel && <Typography variant={"body2"}>{getSalesLeadFollowUpStatusLabels()[status]}</Typography>}
			</HorizontalBox>
		</Tooltip>
	);
}

export function getSalesLeadFollowUpStatusLabels(): Record<SalesLeadFollowUpStatus, string> {
	return {
		overdue: i18n.t("sales_lead_follow_up_status_overdue"),
		no_next_step: i18n.t("sales_lead_follow_up_status_no_next_step"),
		on_track: i18n.t("sales_lead_follow_up_status_on_track"),
	};
}

const followUpStatusColors: Record<SalesLeadFollowUpStatus, string> = {
	overdue: "error.main",
	no_next_step: "warning.main",
	on_track: "success.main",
};

function isSalesLeadFollowUpStatus(status: string | null | undefined): status is SalesLeadFollowUpStatus {
	return status === "overdue" || status === "no_next_step" || status === "on_track";
}

function getSalesLeadFollowUpStatusDescription(status: SalesLeadFollowUpStatus): string {
	switch (status) {
		case "overdue":
			return i18n.t("sales_lead_follow_up_status_overdue_description");
		case "no_next_step":
			return i18n.t("sales_lead_follow_up_status_no_next_step_description");
		case "on_track":
			return i18n.t("sales_lead_follow_up_status_on_track_description");
	}
}
